import { useFinancePayments, useDeletePayment } from "@/api/payments";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Receipt,
  Download,
  SlidersHorizontal,
  FileSpreadsheet,
  FileText,
  X,
  Plus,
  Trash2,
  Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { currencies } from "@/lib/constants";
import { useDebounce } from "@/hooks/useDebounce";
import { startOfDay, endOfDay } from "date-fns";
import { PaymentReceiptModal } from "@/components/payment/PaymentReceiptModal";
import { EditPaymentModal } from "@/components/payment/EditPaymentModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchInput } from "@/components/ui/search-input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { DatePicker } from "@/components/ui/date-picker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { exportToExcel, exportToPDF } from "@/lib/export";
import { AddPaymentForm } from "./components/add-payment-form";

/* ── Helpers ── */
function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/* ── Amount filter parser (supports >, <, =, >=, <=) ── */
function parseAmountFilter(query) {
  const amountRegex = /^([><=]{1,2})(\d+(?:\.\d+)?)$/;
  const match = query.trim().match(amountRegex);
  if (match) {
    const operator = match[1];
    if (![">", "<", "=", ">=", "<="].includes(operator)) return null;
    return { operator, value: parseFloat(match[2]) };
  }
  return null;
}

function matchesAmount(amount, filter) {
  if (!filter) return true;
  const { operator, value } = filter;
  switch (operator) {
    case ">":
      return amount > value;
    case "<":
      return amount < value;
    case "=":
      return amount === value;
    case ">=":
      return amount >= value;
    case "<=":
      return amount <= value;
    default:
      return true;
  }
}

const ITEMS_PER_LOAD = 50;

export default function AdminFinance() {
  const { data: rawPayments, isLoading } = useFinancePayments();
  const [search, setSearch] = useState("");
  const [activeChapter, setActiveChapter] = useState("All");
  const [sortOrder, setSortOrder] = useState("desc");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editPaymentTarget, setEditPaymentTarget] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_LOAD);

  const { mutate: deletePayment, isPending: isDeleting } = useDeletePayment();

  const debouncedSearch = useDebounce(search, 300);

  // Pre-compute search string & timestamp once when data arrives
  const allPayments = useMemo(() => {
    if (!rawPayments) return [];
    return rawPayments.map((p) => ({
      ...p,
      _search:
        `${p.sponsorName}\0${p.paymentId}\0${p.sponsorshipId}\0${p.mahalluName}\0${p.place}\0${p.chapterName}\0${p.sponsorPhone}`.toLowerCase(),
      _ts: new Date(p.createdAt).getTime(),
    }));
  }, [rawPayments]);

  // Chapter filter options
  const chapterOptions = useMemo(() => {
    const names = [
      ...new Set(
        allPayments
          .map((p) =>
            p.chapterName
              ? JSON.stringify({ id: p.chapterId, name: p.chapterName })
              : null,
          )
          .filter(Boolean),
      ),
    ].map((s) => JSON.parse(s));
    return [
      { id: "All", name: "All Chapters" },
      ...names.sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [allPayments]);

  // Advanced filtering + sorting — single pass with pre-computed fields
  const filtered = useMemo(() => {
    // Pre-compute date boundaries once
    const hasDateRange = dateRange.from && dateRange.to;
    const fromTs = hasDateRange ? startOfDay(dateRange.from).getTime() : 0;
    const toTs = hasDateRange ? endOfDay(dateRange.to).getTime() : 0;

    const amountFilter = parseAmountFilter(debouncedSearch);
    const isPartialOp =
      !amountFilter &&
      debouncedSearch.trim() &&
      /^([><=]{1,2})[^0-9]*$/.test(debouncedSearch.trim());
    const searchQ =
      !amountFilter && !isPartialOp && debouncedSearch.trim()
        ? debouncedSearch.toLowerCase()
        : null;

    const typeLower = typeFilter !== "all" ? typeFilter.toLowerCase() : null;

    // Single-pass filter
    const list = [];
    for (let i = 0, len = allPayments.length; i < len; i++) {
      const p = allPayments[i];

      if (activeChapter !== "All" && p.chapterId !== activeChapter) continue;
      if (typeLower && (p.type?.toLowerCase() || "") !== typeLower) continue;
      if (hasDateRange && (p._ts < fromTs || p._ts > toTs)) continue;
      if (amountFilter && !matchesAmount(p.amount, amountFilter)) continue;
      if (searchQ && !p._search.includes(searchQ)) continue;

      list.push(p);
    }

    // Sort using pre-computed _ts (avoids creating Date objects)
    if (sortOrder === "desc") {
      list.sort((a, b) => b._ts - a._ts);
    } else {
      list.sort((a, b) => a._ts - b._ts);
    }

    return list;
  }, [
    allPayments,
    activeChapter,
    typeFilter,
    dateRange,
    debouncedSearch,
    sortOrder,
  ]);

  const displayed = useMemo(
    () => filtered.slice(0, displayedCount),
    [filtered, displayedCount],
  );
  const hasMore = displayedCount < filtered.length;

  useEffect(() => {
    setDisplayedCount(ITEMS_PER_LOAD);
  }, [activeChapter, typeFilter, dateRange, debouncedSearch, sortOrder]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    const { scrollHeight, scrollTop, clientHeight } = document.documentElement;
    if (scrollHeight - scrollTop - clientHeight < 200 && hasMore) {
      setDisplayedCount((c) => c + ITEMS_PER_LOAD);
    }
  }, [hasMore]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Summary
  const summary = useMemo(() => {
    const currencyTotals = {};
    filtered.forEach((p) => {
      const curr = p.currency || "INR";
      currencyTotals[curr] = (currencyTotals[curr] || 0) + (p.amount || 0);
    });
    return { currencyTotals, count: filtered.length };
  }, [filtered]);

  const hasActiveFilters =
    typeFilter !== "all" || dateRange.from || dateRange.to;

  const clearFilters = () => {
    setTypeFilter("all");
    setDateRange({ from: null, to: null });
    setSearch("");
    setActiveChapter("All");
    setSortOrder("desc");
  };

  /* ── Export helpers ── */
  const getExportColumns = () => [
    { header: "Payment ID", key: "paymentId" },
    { header: "Sponsorship ID", key: "sponsorshipId" },
    { header: "Sponsor", key: "sponsorName" },
    { header: "Phone", key: "sponsorPhone" },
    { header: "Type", key: "type" },
    { header: "Location", key: "location" },
    { header: "Chapter", key: "chapterName" },
    { header: "Amount", key: "amountFmt" },
    { header: "Currency", key: "currency" },
    { header: "Date", key: "dateFmt" },
  ];

  const prepareExportRows = (data) =>
    data.map((p) => ({
      paymentId: p.paymentId,
      sponsorshipId: p.sponsorshipId,
      sponsorName: p.sponsorName,
      sponsorPhone: p.sponsorPhone || "",
      type: p.type,
      location: p.type === "Mahallu" ? p.mahalluName : p.place || "",
      chapterName: p.chapterName,
      amountFmt: p.amount,
      currency: p.currency || "INR",
      dateFmt: formatDate(p.createdAt),
    }));

  const handleExportExcel = (useFiltered) => {
    const data = useFiltered ? filtered : allPayments;
    exportToExcel({
      columns: getExportColumns(),
      rows: prepareExportRows(data),
      fileName: `payments-${useFiltered ? "filtered" : "all"}-${new Date().toISOString().slice(0, 10)}`,
    });
  };

  const handleExportPDF = (useFiltered) => {
    const data = useFiltered ? filtered : allPayments;
    exportToPDF({
      columns: getExportColumns(),
      rows: prepareExportRows(data),
      fileName: `payments-${useFiltered ? "filtered" : "all"}-${new Date().toISOString().slice(0, 10)}`,
      title: `Payment History${useFiltered ? " (Filtered)" : ""}`,
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-9 w-full rounded-md" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b -mx-3 md:-mx-6 px-3 md:px-6">
        <div className="h-14 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-tight">Finance</h1>
            <p className="text-xs text-muted-foreground">
              {summary.count} transactions ·{" "}
              {Object.entries(summary.currencyTotals)
                .map(
                  ([curr, total]) =>
                    `${currencies[curr]?.symbol || curr}${total.toLocaleString()}`,
                )
                .join(" + ")}
            </p>
          </div>

          <div className="flex items-center gap-1.5">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Filters</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Export menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8">
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => handleExportExcel(true)}>
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                  Excel (Filtered)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportExcel(false)}>
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                  Excel (All Data)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportPDF(true)}>
                  <FileText className="h-3.5 w-3.5 mr-2 text-red-600" />
                  PDF (Filtered)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportPDF(false)}>
                  <FileText className="h-3.5 w-3.5 mr-2 text-red-600" />
                  PDF (All Data)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Add Payment Button */}
            <Button
              size="sm"
              onClick={() => setShowAddPayment(true)}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add Payment
            </Button>
          </div>
        </div>

        {/* Chapter filter pills */}
        <ScrollArea className="w-full">
          <div className="flex gap-2 pb-2">
            {chapterOptions.map((ch) => (
              <Button
                key={ch.id}
                variant={activeChapter === ch.id ? "default" : "outline"}
                size="sm"
                className="rounded-full shrink-0 h-7 text-xs px-3"
                onClick={() => setActiveChapter(ch.id)}
              >
                {ch.name}
              </Button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Search */}
        <div className="pb-2">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, ID, phone… or amount filter (>500, <=1000)"
          />
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 pb-2">
            <DatePicker
              selected={dateRange.from}
              onSelect={(d) => setDateRange((prev) => ({ ...prev, from: d }))}
              placeholder="From date"
              className="w-auto"
            />
            <DatePicker
              selected={dateRange.to}
              onSelect={(d) => setDateRange((prev) => ({ ...prev, to: d }))}
              placeholder="To date"
              className="w-auto"
            />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Mahallu">Mahallu</SelectItem>
                <SelectItem value="Individual">Individual</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest First</SelectItem>
                <SelectItem value="asc">Oldest First</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-destructive"
                onClick={clearFilters}
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ── Summary Bar ── */}
      <div className="px-4 py-2 flex items-center gap-4 sm:gap-6 border-b bg-muted/30 text-xs">
        <div>
          <span className="text-muted-foreground">Transactions:</span>{" "}
          <span className="font-bold tabular-nums">{summary.count}</span>
        </div>
        {Object.entries(summary.currencyTotals).map(([curr, total]) => (
          <div key={curr}>
            <span className="text-muted-foreground">{curr} Total:</span>{" "}
            <span className="font-bold tabular-nums text-emerald-600">
              {currencies[curr]?.symbol || curr}
              {total.toLocaleString()}
            </span>
          </div>
        ))}
        <div className="ml-auto text-muted-foreground">
          {displayed.length} of {filtered.length}
          {hasMore && " · scroll for more"}
        </div>
      </div>

      {/* ── Excel-style Table ── */}
      <div className="flex-1 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Receipt className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              {search || hasActiveFilters
                ? "No matching payments"
                : "No payments recorded yet"}
            </p>
          </div>
        ) : (
          <table className="w-full text-xs border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[5%]">
                  #
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[9%]">
                  Payment ID
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[9%]">
                  Sponsor ID
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[15%]">
                  Sponsor
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[9%]">
                  Phone
                </th>
                <th className="text-center font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[7%]">
                  Type
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[12%]">
                  Location
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[10%]">
                  Chapter
                </th>
                <th className="text-right font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[9%]">
                  Amount
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 border-r border-border/40 w-[10%]">
                  Date
                </th>
                <th className="text-center font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 w-[5%]"></th>
              </tr>
            </thead>
            <tbody>
              <TooltipProvider delayDuration={300}>
                {displayed.map((p, idx) => {
                  const sym = currencies[p.currency || "INR"]?.symbol || "₹";
                  const location =
                    p.type === "Mahallu" ? p.mahalluName : p.place;

                  return (
                    <tr
                      key={p._id}
                      className={cn(
                        "border-b border-border/30 hover:bg-blue-50/50 transition-colors",
                        idx % 2 === 0 ? "bg-background" : "bg-muted/20",
                      )}
                    >
                      {/* Row # */}
                      <td className="px-3 py-1.5 border-r border-border/30 text-muted-foreground tabular-nums">
                        {idx + 1}
                      </td>
                      {/* Payment ID */}
                      <td className="px-3 py-1.5 border-r border-border/30 font-mono text-muted-foreground">
                        #{p.paymentId}
                      </td>
                      {/* Sponsorship ID */}
                      <td className="px-3 py-1.5 border-r border-border/30 font-mono text-muted-foreground">
                        {p.sponsorshipId}
                      </td>
                      {/* Sponsor */}
                      <td className="px-3 py-1.5 border-r border-border/30">
                        <div className="font-medium truncate max-w-[160px]">
                          {p.sponsorName}
                        </div>
                        {p.careOf?.name && (
                          <div className="text-[10px] text-muted-foreground truncate">
                            c/o {p.careOf.name}
                          </div>
                        )}
                      </td>
                      {/* Phone */}
                      <td className="px-3 py-1.5 border-r border-border/30 text-muted-foreground">
                        {p.sponsorPhone || "—"}
                      </td>
                      {/* Type */}
                      <td className="px-3 py-1.5 border-r border-border/30 text-center">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[9px] px-1.5 py-0",
                            p.type === "Mahallu"
                              ? "border-violet-200 text-violet-700 bg-violet-50"
                              : "border-sky-200 text-sky-700 bg-sky-50",
                          )}
                        >
                          {p.type}
                        </Badge>
                      </td>
                      {/* Location */}
                      <td className="px-3 py-1.5 border-r border-border/30 text-muted-foreground truncate max-w-[140px]">
                        {location || "—"}
                      </td>
                      {/* Chapter */}
                      <td className="px-3 py-1.5 border-r border-border/30 truncate max-w-[120px]">
                        {p.chapterName || "—"}
                      </td>
                      {/* Amount */}
                      <td className="px-3 py-1.5 border-r border-border/30 text-right font-bold tabular-nums text-emerald-600">
                        {sym}
                        {p.amount?.toLocaleString()}
                      </td>
                      {/* Date */}
                      <td className="px-3 py-1.5 border-r border-border/30 text-muted-foreground whitespace-nowrap">
                        <div>{formatDate(p.createdAt)}</div>
                        <div className="text-[10px]">
                          {formatTime(p.createdAt)}
                        </div>
                      </td>
                      {/* Actions */}
                      <td className="px-3 py-1.5 text-center flex items-center justify-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                setReceiptTarget({
                                  paymentId: p.paymentId,
                                  name: p.sponsorName,
                                  place: p.mahalluName || p.place || "",
                                  amount: p.amount,
                                  currency: p.currency || "INR",
                                  createdAt: p.createdAt,
                                  phone: p.sponsorPhone || p.phone || "",
                                })
                              }
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            Download Receipt
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-blue-600/70 hover:text-blue-600 hover:bg-blue-50"
                              onClick={() => setEditPaymentTarget(p)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            Edit Payment
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteId(p._id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            Delete Payment
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </TooltipProvider>
            </tbody>
          </table>
        )}
      </div>

      {/* ── Load more indicator ── */}
      {hasMore && (
        <div className="text-center py-3 text-xs text-muted-foreground border-t">
          Scroll down for more · Showing {displayed.length} of {filtered.length}
        </div>
      )}

      {/* Receipt Modal */}
      {receiptTarget && (
        <PaymentReceiptModal
          isOpen={!!receiptTarget}
          onClose={() => setReceiptTarget(null)}
          payment={receiptTarget}
        />
      )}

      {/* Edit Payment Modal */}
      <EditPaymentModal
        isOpen={!!editPaymentTarget}
        onClose={() => setEditPaymentTarget(null)}
        payment={editPaymentTarget}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              payment record and update any linked sponsorship details.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteId) {
                  deletePayment(deleteId);
                  setDeleteId(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Payment Dialog */}
      <Dialog open={showAddPayment} onOpenChange={setShowAddPayment}>
        <DialogContent className="max-w-2xl p-4 pt-6">
          <DialogHeader className="pb-4">
            <DialogTitle>Add Direct Payment</DialogTitle>
            <DialogDescription className="text-xs">
              Record a standalone payment not tied to a sponsorship
            </DialogDescription>
          </DialogHeader>
          <AddPaymentForm onSuccess={() => setShowAddPayment(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
